import React, { useState } from 'react';
import { CycleEntry } from '../../types';

interface MissedPeriodInfo {
  missedMonth: string;
  expectedDate: string;
  gapDays: number;
}

interface MissedPeriodAlertProps {
  cycles: CycleEntry[];
}

function detectMissedPeriods(cycles: CycleEntry[]): MissedPeriodInfo[] {
  if (cycles.length < 2) return [];

  const sorted = [...cycles].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

  // Use a standard 28-day cycle as baseline.
  // If we have 3+ cycles, use the shortest gap as the likely real cycle length.
  const STANDARD_CYCLE = 28;
  const gaps: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const gap = Math.round(
      (new Date(sorted[i].startDate).getTime() - new Date(sorted[i - 1].startDate).getTime()) /
        (1000 * 60 * 60 * 24)
    );
    gaps.push(gap);
  }

  // For the expected cycle length, use the minimum gap if it's reasonable (21-35 days),
  // otherwise fall back to 28 days. This handles the case where you have Jan + March
  // logged - the gap is ~55 days, which is way more than a normal cycle.
  const shortGaps = gaps.filter(g => g >= 21 && g <= 35);
  const expectedLength = shortGaps.length > 0
    ? Math.round(shortGaps.reduce((a, b) => a + b, 0) / shortGaps.length)
    : STANDARD_CYCLE;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const missed: MissedPeriodInfo[] = [];

  for (let i = 1; i < sorted.length; i++) {
    const prevDate = new Date(sorted[i - 1].startDate);
    const nextDate = new Date(sorted[i].startDate);
    const gapDays = Math.round(
      (nextDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // If the gap is more than 1.4x the expected cycle length, at least one period was missed
    // For example: expected 28 days, gap 46 days → missed 1 period (Feb)
    if (gapDays >= expectedLength * 1.4) {
      // Calculate how many full cycles fit in the gap
      // e.g. 46 / 28 = 1.64 → round down = 1 → that means 1 cycle happened but we expected 2
      // So missed = round(gapDays / expectedLength) - 1, minimum 1
      const expectedCycles = Math.round(gapDays / expectedLength);
      const missedCount = Math.max(expectedCycles - 1, 1);
      for (let m = 1; m <= missedCount; m++) {
        const expDate = new Date(
          prevDate.getTime() + m * expectedLength * 24 * 60 * 60 * 1000
        );
        missed.push({
          missedMonth: `${monthNames[expDate.getMonth()]} ${expDate.getFullYear()}`,
          expectedDate: expDate.toISOString().split('T')[0],
          gapDays,
        });
      }
    }
  }

  return missed;
}

const MissedPeriodAlert: React.FC<MissedPeriodAlertProps> = ({ cycles }) => {
  const [dismissed, setDismissed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const missedPeriods = detectMissedPeriods(cycles);

  if (missedPeriods.length === 0 || dismissed) return null;

  return (
    <div className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-5 shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <span className="text-3xl">🔴</span>
          <div className="flex-1">
            <h4 className="font-bold text-orange-800 text-lg mb-1">
              Missed Period{missedPeriods.length > 1 ? 's' : ''} Detected
            </h4>
            <p className="text-sm text-orange-700 mb-3">
              Based on your logged cycles, it looks like you missed{' '}
              {missedPeriods.length === 1
                ? `your period in ${missedPeriods[0].missedMonth}`
                : `periods in ${missedPeriods.map((p) => p.missedMonth).join(', ')}`}
              . This could indicate a health concern.
            </p>

            <div className="space-y-2">
              {missedPeriods.map((mp, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 bg-white/70 rounded-xl px-3 py-2"
                >
                  <span className="text-orange-500">⚠️</span>
                  <span className="text-sm font-medium text-orange-800">
                    {mp.missedMonth}
                  </span>
                  <span className="text-xs text-orange-600">
                    (expected around {new Date(mp.expectedDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })})
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-3 p-3 bg-white/60 rounded-xl">
              <p className="text-sm font-medium text-orange-800">
                💡 A missed period can be caused by stress, weight changes, hormonal imbalances,
                or other factors. Consider consulting a healthcare provider if this is unexpected.
              </p>
            </div>

            <button
              onClick={() => setShowDetails(!showDetails)}
              className="mt-3 text-sm font-medium text-orange-700 underline hover:no-underline"
            >
              {showDetails ? 'Hide Details' : 'Learn More About Missed Periods'}
            </button>

            {showDetails && (
              <div className="mt-3 p-4 bg-white/70 rounded-xl space-y-3 text-sm text-orange-800">
                <div>
                  <h5 className="font-semibold mb-1">Common causes of missed periods:</h5>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Stress or emotional changes</li>
                    <li>Significant weight loss or gain</li>
                    <li>Excessive exercise</li>
                    <li>Polycystic ovary syndrome (PCOS)</li>
                    <li>Thyroid disorders</li>
                    <li>Pregnancy</li>
                    <li>Perimenopause</li>
                    <li>Certain medications</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold mb-1">When to see a doctor:</h5>
                  <ul className="list-disc list-inside space-y-1">
                    <li>You've missed 3 or more periods in a row</li>
                    <li>You're not pregnant and have missed a period</li>
                    <li>You experience other symptoms like pain or unusual discharge</li>
                  </ul>
                </div>
                <p className="text-xs italic pt-2 border-t border-orange-200">
                  This is for informational purposes only. Always consult a healthcare professional.
                </p>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => setDismissed(true)}
          className="ml-2 text-orange-400 hover:text-orange-600 transition-colors"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default MissedPeriodAlert;
